import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["questionKey"];
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Image } from 'foundations-components/transitional/components/Image';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { createToObject } from 'pricing-pages-lib/utils/queryParamUtils';
import { useCallback, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import BannerSection from '../components/banners/BannerSection';
import { Inbound25CopyChange } from '../components/common/i18n/CopyChangeComponents';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import { ERROR_SEVERITY } from '../components/error-boundary/error-severity';
import { useErrorBoundary } from '../components/error-boundary/useErrorBoundary';
import withErrorBoundary from '../components/error-boundary/withErrorBoundary';
import Faq from '../components/faqs/Faq';
import Question from '../components/faqs/Question';
import BuyAdditionalCreditsCard from '../components/hubspot-credits/BuyAdditionalCreditsCard';
import UnlockAISection from '../components/hubspot-credits/UnlockAISection';
import { getHubSpotCreditsHeroImage } from '../components/key-features/getLocalizedImages';
import HeaderLayoutWrapper from '../components/layout/HeaderLayoutWrapper';
import { OWNERSHIP_CHECK_FAILED, PRICING_AND_PURCHASING_FAILED_STATUS } from '../constants/ErrorCauses';
import { FaqsConfig } from '../constants/faqs/FaqsConfig';
import { NavItemEnum } from '../constants/routing/NavItems';
import { HUBSPOT_CREDITS_VALUE_PROPOSITIONS } from '../constants/tracking/TrackingSources';
import { useAnchorLinkScroll } from '../hooks/htmlElementHooks';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useRedirectIfIneligible } from '../hooks/useRedirectIfIneligible';
import { useAppSelector } from '../reducers';
import { isUngatedForHubSpotCreditsSelector } from '../selectors/GatingSelectors';
import { hasGatesLoadedSelector, hasOnlyOwnershipCheckFailedSelector, hasPricingAndPurchasingFailedSelector } from '../selectors/LoadingStateSelectors';
import { ownedHubSpotCreditsTotalSelector } from '../selectors/ProductInventorySelectors';
import { trackPageView } from '../utils/tracking/trackingEventUtils';
import { useQueryParamTerm, useQueryParamsEffect } from '../utils/urls/appQueryParamUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const HubSpotCreditsPageInbound25 = () => {
  useAnchorLinkScroll();
  useDocumentTitle('hubspot-credits');
  const navItem = NavItemEnum.HUBSPOT_CREDITS;
  const faqsConfig = FaqsConfig[navItem];
  const isUngatedForHubSpotCredits = useAppSelector(isUngatedForHubSpotCreditsSelector);
  const isAppReady = useGlobalLoading();
  const hasPricingAndPurchasingFailed = useAppSelector(hasPricingAndPurchasingFailedSelector);
  const hasOnlyOwnershipCheckFailed = useAppSelector(hasOnlyOwnershipCheckFailedSelector);
  const hasGatesLoaded = useAppSelector(hasGatesLoadedSelector);
  const ownsAdditionalCredits = useAppSelector(state => {
    if (!isAppReady) return false;
    const ownedQuantity = ownedHubSpotCreditsTotalSelector(state);
    return ownedQuantity > 0;
  });
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const {
    showBoundary
  } = useErrorBoundary();

  /**
   * On public, there's a race condition where pricing and purchasing has succeeded, but the activeFeatureFlags have not been populated yet (@see PublicMetaDataActions). So sometimes this condition gets evaluated to true and redirects to /suite before we actually confirm if the user is gated for Breeze Intelligence.
   */
  useRedirectIfIneligible({
    condition: useCallback(() => {
      if (!hasGatesLoaded) {
        return;
      }
      return !isUngatedForHubSpotCredits;
    }, [hasGatesLoaded, isUngatedForHubSpotCredits])
  });
  useEffect(() => {
    getUsageTracker().setProperties({
      defaultedTab: '',
      tab: ''
    });
    trackPageView({
      screen: navItem
    });
  }, [navItem]);
  if (hasPricingAndPurchasingFailed) {
    if (hasOnlyOwnershipCheckFailed) {
      showBoundary(new Error(OWNERSHIP_CHECK_FAILED));
    }
    showBoundary(new Error(PRICING_AND_PURCHASING_FAILED_STATUS));
  }
  if (!isAppReady || !hasGatesLoaded) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      minHeight: 200,
      size: "md",
      grow: true
    });
  }
  return /*#__PURE__*/_jsxs(HeaderLayoutWrapper, {
    navItem: navItem,
    navMarkerName: "HUBSPOT_CREDITS_PAGE",
    children: [/*#__PURE__*/_jsx(BannerSection, {
      screen: navItem
    }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "row",
      gap: "800",
      flexWrap: "wrap",
      justifyContent: "center",
      alignItems: "center",
      padding: "1300",
      children: [/*#__PURE__*/_jsx(View, {
        as: "header",
        flexBasis: "100%",
        flex: 1,
        children: /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          gap: "600",
          children: [/*#__PURE__*/_jsx(Text, {
            as: "h1",
            typography: "heading-300",
            children: /*#__PURE__*/_jsx(Inbound25CopyChange, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "pricingPage.pages.products.tabs.hubspot-credits.pageTitle"
              })
            })
          }), /*#__PURE__*/_jsx(Text, {
            typography: "heading-800",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "pricingPage.pages.hubspot-credits.hero.headline"
            })
          }), /*#__PURE__*/_jsx(Text, {
            typography: "body-100",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "pricingPage.pages.hubspot-credits.hero.description"
            })
          }), ownsAdditionalCredits ? null : /*#__PURE__*/_jsx(Text, {
            typography: "body-100",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "pricingPage.pages.hubspot-credits.hero.disclaimer"
            })
          })]
        })
      }), /*#__PURE__*/_jsx(Flex, {
        flexBasis: "100%",
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        children: ownsAdditionalCredits ? /*#__PURE__*/_jsx(BuyAdditionalCreditsCard, {
          trackingSource: HUBSPOT_CREDITS_VALUE_PROPOSITIONS,
          screen: navItem
        }) : /*#__PURE__*/_jsx(Image, {
          src: getHubSpotCreditsHeroImage(),
          alt: I18n.text('pricingPage.pages.hubspot-credits.hero.alt')
        })
      })]
    }), /*#__PURE__*/_jsx(UnlockAISection, {}), /*#__PURE__*/_jsx(Faq, {
      children: faqsConfig.map(_ref => {
        let {
            questionKey
          } = _ref,
          passthroughOptions = _objectWithoutPropertiesLoose(_ref, _excluded);
        return /*#__PURE__*/_jsx(Question, Object.assign({
          questionKey: questionKey
        }, passthroughOptions), questionKey);
      })
    })]
  });
};
export default withErrorBoundary(HubSpotCreditsPageInbound25, {
  fallback: /*#__PURE__*/_jsx(Redirect, {
    to: createToObject('/suite')
  }),
  errorBoundaryName: 'HubSpotCreditsPageInbound25',
  severity: ERROR_SEVERITY.HIGH
});